import { useState } from 'react'
import { uniqBy } from 'lodash'
import { BarChart } from '@mui/x-charts/BarChart'
import type { SxProps } from '@mui/material'
import { Paper, CircularProgress, Box, Grid } from '@mui/material'
import { useGetMembersByTierQuery } from 'store/dashboard'
import { useGetCarModelDropdownListByQuery, useGetCityDropdownListByQuery } from 'store/dropdown'
import AutocompleteField from 'components/autocomplete/autocomplete'
import ChartTitle from './ChartTitle'
import NoDataAvailable from './NoDataAvailable'
import TierFilter from './TierFilter'
import { barChartSlotProps, chartSx } from './chartDefaults'

const MemberTiersLevelChart = () => {
  const [selectedCarModel, setSelectedCarModel] = useState<{ label: string; id: number } | null>(
    null
  )
  const [city, setCity] = useState<{ label: string; id: number } | null>(null)
  const [selectedTiers, setSelectedTiers] = useState<string[]>([])

  const { data: carModels, isLoading: loadingCar } = useGetCarModelDropdownListByQuery()
  const { data: cities, isLoading: loadingCity } = useGetCityDropdownListByQuery()
  const { data, isLoading, isError, refetch, isFetching } = useGetMembersByTierQuery({
    carModelId: selectedCarModel ? Number(selectedCarModel.id) : undefined,
    cityId: city ? Number(city.id) : undefined,
    memberTiers: selectedTiers.length > 0 ? selectedTiers.join(',') : undefined
  })

  function barChartSx(dat: NonNullable<typeof data>) {
    const sx: Record<string, SxProps> = {}

    dat.forEach((d, i) => {
      sx[`& rect:nth-child(${i + 1})`] = {
        fill: d.tierColor
      }
    })

    return sx;
  }

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 'none'
      }}
      elevation={0}
    >
      <ChartTitle tooltip='Shows the number of members in each tier level'>
        Member Tier Level
      </ChartTitle>

      {isLoading || isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <NoDataAvailable variant='error' onRefresh={refetch} />
      ) : !data ? (
        <NoDataAvailable onRefresh={refetch} />
      ) : data.length === 0 ? (
        <NoDataAvailable onRefresh={refetch} />
      ) : (
        <Box sx={{ width: '100%', height: 300 }}>
          <BarChart
            yAxis={[{ scaleType: 'band', data: data?.map?.((d) => d.tierName) || [] }]}
            xAxis={[{ tickMinStep: 1 }]}
            series={[
              {
                label: 'Total',
                data: data?.map?.((d) => d.total) || []
              }
            ]}
            slotProps={{ ...barChartSlotProps, legend: { hidden: true } }}
            sx={{ ...barChartSx(data), ...chartSx }}
            layout='horizontal'
          />
        </Box>
      )}

      <Grid container direction='row' spacing={2} p={2}>
        <Grid item xs={4} pr={1} data-download-hide='true'>
          <AutocompleteField<number>
            data-download-hide='true'
            options={uniqBy(carModels ?? [], 'id').map((x) => ({ label: x.name, id: x.id }))}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            isLoading={loadingCar}
            value={selectedCarModel}
            onChange={(_, val) => setSelectedCarModel(val)}
            label='Car Model'
          />
        </Grid>

        <Grid item xs={4} pr={1} data-download-hide='true'>
          <AutocompleteField<number>
            data-download-hide='true'
            options={uniqBy(cities ?? [], 'id').map((x) => ({ label: x.name, id: x.id }))}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            isLoading={loadingCity}
            value={city}
            onChange={(_, val) => setCity(val)}
            label='City'
          />
        </Grid>

        <Grid item xs={4} pr={1} data-download-hide='true'>
          <TierFilter value={selectedTiers} onChange={setSelectedTiers} />
        </Grid>
      </Grid>
    </Paper>
  )
}

export default MemberTiersLevelChart
